import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { ArrowRight, CreditCard, ShieldCheck, X } from "lucide-react";
import { useSelector } from "react-redux";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { isPendingSignup, type SignupAuth } from "./activation";
import { getStripePromise } from "../../lib/stripeClient";
import {
  createVerificationApi,
  VerificationError,
  type VerificationResponse,
  type VerificationSnapshot,
  type VerificationScope,
} from "./verificationApi";
import LiveVerificationWizard from "./LiveVerificationWizard";
import { VerificationCard } from "./VerificationCard";
import { SignupSupport } from "./SignupSupport";
import { RegistrationFeedback } from "./RegistrationFeedback";
import formatPhoneForUser from "../../utils/formatPhoneForUser";
import { takeSignupPaymentReturn } from "./stripeReturn";
import "../signup-prototype/signup.css";
import "./signup.css";

export function PendingBillingNotice() {
  return (
    <section
      className="signup-pending-note"
      aria-label="Billing starts after texting is ready"
    >
      <h2>No payment today.</h2>
      <p>
        Your account is waiting for texting setup. Your subscription starts only
        when your business number is approved and ready to text.
      </p>
      <Link className="sp-button-secondary" to="/onboarding">
        Back to Get started
        <ArrowRight size={16} />
      </Link>
    </section>
  );
}

export default function LiveOnboardingPage() {
  const auth = useSelector((state: { auth?: SignupAuth }) => state.auth);
  if (!isPendingSignup(auth)) return <Navigate to="/inbox" replace />;
  if (auth?.user?.accessLevel !== 'owner') return <main className="signup-onboarding-page live-signup-onboarding"><header className="signup-dashboard-heading"><div><span className="sp-eyebrow">WELCOME TO YOUR DASHBOARD</span><h1>Make yourself at home.</h1><p>You can explore your dashboard while your account owner finishes texting setup.</p></div></header><section className="signup-pending-note"><h2>Your account owner handles texting setup.</h2><p>Customer texting becomes available after your number is approved and ready and the first payment succeeds.</p><Link className="sp-button-secondary" to="/walkthroughs">Browse walkthroughs<ArrowRight size={16} /></Link></section></main>;
  const scope = { businessId: auth?._id || "", userId: auth?.user?._id || "" };
  return (
    <VerificationPage
      key={`${scope.businessId}:${scope.userId}`}
      scope={scope}
      name={auth?.name || "your business"}
    />
  );
}

function VerificationPage({
  scope,
  name,
}: {
  scope: VerificationScope;
  name: string;
}) {
  const navigate = useNavigate();
  const api = useMemo(
    () => createVerificationApi(scope),
    [scope.businessId, scope.userId],
  );
  const [data, setData] = useState<VerificationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useSearchParams();
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const alive = useRef(false);
  const pending = useRef(false);
  const generation = useRef(0);
  const controller = useRef<AbortController | undefined>(undefined);
  const apply = (value: VerificationResponse) => {
    if (!alive.current) return;
    setData(value);
    window.dispatchEvent(new Event('signup-verification-updated'));
    if (
      "state" in value &&
      !["draft", "correction_required"].includes(value.state)
    )
      setOpen(false);
  };
  const load = useCallback(
    async (showLoading = true) => {
      if (pending.current) return;
      const request = ++generation.current;
      controller.current?.abort();
      controller.current = new AbortController();
      if (showLoading) setLoading(true);
      try {
        const value = await api.load(controller.current.signal);
        if (alive.current && request === generation.current) {
          apply(value);
          if (showLoading) setError("");
        }
      } catch (reason) {
        if (
          alive.current &&
          request === generation.current &&
          !controller.current.signal.aborted &&
          showLoading
        )
          setError(
            reason instanceof Error
              ? reason.message
              : "We couldn’t load your setup. Please try again.",
          );
      } finally {
        if (alive.current && request === generation.current) setLoading(false);
      }
    },
    [api],
  );
  const perform = useCallback(
    async (work: () => Promise<any>): Promise<any> => {
      if (pending.current || !alive.current) return null;
      pending.current = true;
      ++generation.current;
      controller.current?.abort();
      setBusy(true);
      setError("");
      setFieldErrors(undefined);
      try {
        const value = await work();
        if (!alive.current) return null;
        apply(value.snapshot || value);
        return value;
      } catch (reason) {
        if (!alive.current) return null;
        // Reconcile ambiguous responses and stale revisions before allowing another mutation.
        try {
          const fresh = await api.load();
          if (alive.current) apply(fresh);
        } catch {
          /* Keep the last confirmed state and show the original failure. */
        }
        if (!alive.current) return null;
        setError(
          reason instanceof VerificationError && reason.code === "SIGNUP_REVIEW_REQUIRED"
            ? "We’re reviewing your business details. Your setup is saved. Contact our team if you need a hand."
            : reason instanceof VerificationError && reason.code === "SETUP_CHANGED"
            ? "Your saved setup was updated. Your entries are still here. Review any changed details, then continue."
            : reason instanceof VerificationError && reason.status === 409
            ? `${reason.message} Review the latest details and try again.`
            : reason instanceof Error
              ? reason.message
              : "We couldn’t save that step. Please try again.",
        );
        setFieldErrors(
          reason instanceof VerificationError ? reason.fieldErrors : undefined,
        );
        return null;
      } finally {
        pending.current = false;
        if (alive.current) setBusy(false);
      }
    },
    [api],
  );
  useEffect(() => {
    alive.current = true;
    const setupIntentId = takeSignupPaymentReturn();
    if (setupIntentId && /^seti_[A-Za-z0-9]+$/.test(setupIntentId))
      void perform(() => api.confirm(setupIntentId)).then(() => {
        if (alive.current) setLoading(false);
      });
    else void load();
    return () => {
      alive.current = false;
      ++generation.current;
      controller.current?.abort();
    };
  }, [api, load, perform]);
  const snapshot =
    data && "version" in data ? (data as VerificationSnapshot) : null;
  useEffect(() => {
    if (snapshot?.state === "active") window.location.replace("/inbox");
  }, [snapshot?.state]);
  useEffect(() => {
    if (
      !snapshot ||
      [
        "draft",
        "correction_required",
        "cancelled",
        "support_required",
        "active",
      ].includes(snapshot.state)
    )
      return;
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible" && !open) void load(false);
    }, 15000);
    return () => window.clearInterval(timer);
  }, [snapshot?.state, load, open]);
  const recoverPayment = async () => {
    const result = await perform(() => api.recover());
    if (
      !alive.current ||
      !result?.clientSecret ||
      !result.snapshot.readiness.ready ||
      result.snapshot.billing.status !== "requires_action"
    )
      return;
    setBusy(true);
    pending.current = true;
    try {
      const stripe = await getStripePromise();
      if (!alive.current) return;
      if (!stripe)
        throw new Error(
          "We couldn’t open secure payment confirmation. Please try again.",
        );
      const confirmation = await stripe.confirmCardPayment(result.clientSecret);
      if (!alive.current) return;
      if (confirmation.error)
        setError(
          confirmation.error.message ||
            "We couldn’t confirm your payment. Please try again.",
        );
    } catch (reason) {
      if (alive.current)
        setError(
          reason instanceof Error
            ? reason.message
            : "We couldn’t confirm your payment. Please try again.",
        );
    } finally {
      pending.current = false;
      if (alive.current) {
        setBusy(false);
        void load(false);
      }
    }
  };
  const status = snapshot?.state;
  const staging = snapshot?.environment === "staging";
  const reviewHeld = snapshot?.error?.code === "SIGNUP_REVIEW_REQUIRED";
  const resumable = status === "cancelled" && snapshot?.canResume === true;
  const correction = snapshot?.registration.correction;
  const canStartCorrection = !reviewHeld && snapshot?.registration.rejection?.canCorrect && correction?.status !== "editing";
  const editable = !reviewHeld && (status === "draft" || (status === "correction_required" && (snapshot?.canCancel || correction?.status === "editing")));
  useEffect(() => {
    if (search.get('verify') !== '1' || loading || !snapshot) return;
    if (editable && snapshot.offer) setOpen(true);
    const next = new URLSearchParams(search); next.delete('verify'); setSearch(next, { replace: true });
  }, [search, setSearch, loading, snapshot, editable]);
  const title = correction?.status === "assistance_requested" ? "Your updates are with our team." : reviewHeld ? "We’re reviewing your business details." : staging && status === "pending_approval" ? "Simulating carrier approval." : !snapshot
    ? "Your workspace is here. Let’s get texting ready."
    : {
        draft: "Let’s get your business ready to text.",
        submitted: "Your setup is on its way.",
        working: "We’re getting your number ready.",
        pending_approval: "Your registration is being reviewed.",
        correction_required: "A few details need your attention.",
        payment_required: "Your payment needs attention.",
        active: "Your business is ready.",
        cancelled: "Your setup has been cancelled.",
        support_required: "We’ll help you finish setup.",
      }[snapshot.state];
  const description = correction?.status === "assistance_requested" ? "Your updated details are saved for our support team. Our team will help with resubmission. These updates haven’t been resubmitted to carriers." : reviewHeld ? "Your setup is saved. Our team will review the details before the next step. Contact us if you need a hand." : staging && ["submitted", "working", "pending_approval"].includes(status || "") ? "Your test setup is saved. Approval is simulated in about a minute, then Stripe processes a test payment. Nothing is sent to carriers or customers." : staging && status === "payment_required" ? "Complete your Stripe test payment below. Customer texting stays off after test activation." : !snapshot
    ? "Our team will help verify your business, add your payment method, and set up your texting number."
    : (!snapshot.registration.rejection && snapshot.registration.message) ||
      {
        draft:
          "Add your card, confirm your business details, and choose a texting number. We’ll guide you through each step.",
        submitted:
          "Your details are saved. We’ll prepare your number and submit your registration.",
        working:
          "You can leave this page and return anytime. Your progress is saved.",
        pending_approval:
          "We’ll update this page as your registration moves forward. Billing waits until your number is approved and ready.",
        correction_required:
          "Review the requested changes, then submit your updated details.",
        payment_required:
          "Complete the payment step below. Texting becomes available after your first payment succeeds.",
        active: "Opening your dashboard…",
        cancelled: resumable
          ? "Your account and saved steps are still here. Continue setup whenever you’re ready."
          : "This setup includes an earlier registration or billing step. Contact our team so we can safely reopen it. Your account and details are saved.",
        support_required: "Contact our team so we can help with the next step.",
      }[snapshot.state];
  return (
    <main className="signup-onboarding-page live-signup-onboarding">
      <header className="signup-dashboard-heading">
        <div>
          <span className="sp-eyebrow">WELCOME TO YOUR DASHBOARD</span>
          <h1>Let’s get {name} ready.</h1>
          <p>Your email is confirmed and your account is saved.</p>
        </div>
      </header>
      <section className="signup-pending-note" aria-label="Texting setup">
        <span className="signup-status">
          <ShieldCheck size={17} />
          Texting setup
        </span>
        <h2>{loading ? "Loading your setup…" : title}</h2>
        {!loading && (
          <>
            <p>{description}</p>
            {snapshot && <RegistrationFeedback registration={snapshot.registration} />}
            {snapshot?.number.assigned && (
              <p>
                {staging ? "Your fictional test number: " : "Your texting number: "}<strong>{formatPhoneForUser(snapshot.number.assigned)}</strong>
              </p>
            )}
            <p className="signup-pending-billing">
              <CreditCard size={20} />
              <span>
                {staging ? "Stripe test mode: only simulated payments are processed. No real money moves."
                  : snapshot?.billing.status === "paid"
                  ? "Your first payment is confirmed."
                  : snapshot?.billing.status === "processing"
                    ? "Your first payment is processing. We’ll update your account when it’s confirmed."
                    : snapshot?.billing.status === "requires_action" ||
                        snapshot?.billing.status === "payment_failed"
                      ? "Your first payment needs attention. Texting stays paused until payment succeeds."
                      : snapshot?.state === 'cancelled'
                        ? resumable ? 'Nothing has been charged. Billing starts only when your number is approved and ready to text.' : 'Contact our team if you have a billing question about this setup.'
                        : snapshot && !['draft', 'correction_required'].includes(snapshot.state)
                          ? 'Billing starts only when your number is approved and ready to text. We’ll show your payment status here.'
                          : "Nothing has been charged. Billing starts only when your number is approved and ready to text."}
              </span>
            </p>
          </>
        )}
        {error && !open && (
          <div className="signup-verification-error" role="alert">
            {error}
          </div>
        )}
        <div className="signup-verification-actions">
          {!loading && resumable && snapshot && (
            <button className="sp-button" disabled={busy} onClick={() => void perform(() => api.resume(snapshot.revision)).then(result => {
              if (result?.state === "draft") { setConfirmCancel(false); setOpen(true); }
            })}>{busy ? "Opening your saved setup…" : "Continue setup"}<ArrowRight size={16} /></button>
          )}
          {!loading && canStartCorrection && snapshot && (
            <button className="sp-button" disabled={busy} onClick={() => void perform(() => api.startCorrection(snapshot.revision)).then(result => {
              if (result?.registration?.correction?.status === "editing") setOpen(true);
            })}>{busy ? "Opening saved details…" : "Review business details"}<ArrowRight size={16} /></button>
          )}
          {!loading && editable && snapshot?.offer && (
            <button
              className="sp-button"
              disabled={busy}
              onClick={() => setOpen(true)}
            >
              {status === "correction_required"
                ? "Review business details"
                : "Verify your business for texting"}
              <ArrowRight size={16} />
            </button>
          )}
          {!loading &&
            snapshot &&
            !reviewHeld &&
            !editable &&
            !["active", "cancelled", "support_required"].includes(
              snapshot.state,
            ) && (
              <button
                className="sp-button-secondary"
                disabled={busy}
                onClick={() => void perform(() => api.refresh())}
              >
                {busy ? "Checking…" : "Check for updates"}
              </button>
            )}
          {!loading &&
            !reviewHeld && snapshot?.state === "payment_required" &&
            snapshot?.readiness.ready &&
            ["requires_action", "payment_failed"].includes(
              snapshot.billing.status,
            ) && (
              <button
                className="sp-button"
                disabled={busy}
                onClick={() => void recoverPayment()}
              >
                Complete payment
                <ArrowRight size={16} />
              </button>
            )}
          {!loading && !reviewHeld && snapshot?.state === "payment_required" && (
            <button
              className="sp-button-secondary"
              disabled={busy}
              onClick={() => setPaymentOpen(true)}
            >
              Use a different card
            </button>
          )}
          {!loading &&
            (!snapshot ||
              !snapshot.offer ||
              reviewHeld ||
              snapshot.state === "support_required" ||
              (snapshot.state === "cancelled" && !resumable)) && (
              <a className="sp-button" href="mailto:support@chekkit.io">
                Get help with texting setup
                <ArrowRight size={16} />
              </a>
            )}
          {!loading && error && (
            <button
              className="sp-button-secondary"
              disabled={busy}
              onClick={() => void load()}
            >
              Reload setup
            </button>
          )}
        </div>
        <SignupSupport />
        {snapshot?.canCancel &&
          !confirmCancel && (
            <button
              type="button"
              className="signup-cancel-setup"
              disabled={busy}
              onClick={() => setConfirmCancel(true)}
            >
              Cancel setup
            </button>
          )}
        {confirmCancel && snapshot?.canCancel && (
          <div className="signup-cancel-confirm">
            <p>
              Cancel this setup request? Your account and completed steps will stay saved.
              You can continue setup later.
            </p>
            <div className="signup-verification-actions">
              <button
                className="sp-button-secondary"
                disabled={busy}
                onClick={() => setConfirmCancel(false)}
              >
                Keep setting up
              </button>
              <button
                className="sp-button"
                disabled={busy}
                onClick={() =>
                  void perform(() => api.cancel(snapshot.revision)).then(
                    (result) => {
                      if (result) setConfirmCancel(false);
                    },
                  )
                }
              >
                Cancel setup request
              </button>
            </div>
          </div>
        )}
      </section>
      {open && snapshot && editable && (
        <LiveVerificationWizard
          snapshot={snapshot}
          api={api}
          busy={busy}
          error={error || (snapshot.registration.rejection ? "" : snapshot.error?.message) || ""}
          fieldErrors={fieldErrors || snapshot.error?.fieldErrors}
          perform={perform}
          onClose={() => setOpen(false)}
          onSubmitted={() => navigate("/walkthroughs", { replace: true, state: { verificationSubmitted: true } })}
        />
      )}
      {paymentOpen && snapshot?.state === "payment_required" && (
        <Dialog.Root
          open
          onOpenChange={(value) => {
            if (!busy) setPaymentOpen(value);
          }}
        >
          <Dialog.Portal>
            <Dialog.Overlay className="sv-overlay" />
            <Dialog.Content
              className="signup-prototype sv-modal live-verification sv-card-only"
              onInteractOutside={(event) => {
                if (busy) event.preventDefault();
              }}
              onEscapeKeyDown={(event) => {
                if (busy) event.preventDefault();
              }}
            >
              <Dialog.Close
                className="sv-close"
                disabled={busy}
                aria-label="Close card form"
              >
                <X size={18} />
              </Dialog.Close>
              <div className="sv-main">
                <header className="sv-header">
                  <Dialog.Title>Update your payment card</Dialog.Title>
                  <Dialog.Description>
                    Save a different card for your first payment. Your business
                    registration stays saved.
                  </Dialog.Description>
                </header>
                {error && (
                  <p className="sv-error" role="alert">
                    {error}
                  </p>
                )}
                <VerificationCard
                  replacement
                  staging={staging}
                  prepare={() => perform(() => api.setup(snapshot.revision))}
                  confirm={(id) => perform(() => api.confirm(id))}
                  onSaved={() => setPaymentOpen(false)}
                />
                <SignupSupport />
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}
    </main>
  );
}
